import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import { formatCents } from "utils/api-errors";
import { formatLeadDate } from "layouts/leads/components/leadFormat";

// Texto de exemplo para o blur. NUNCA dado real: a API nem envia os dados de pedido bloqueado.
const PLACEHOLDER = {
  client: "Nome do Cliente",
  guests: "100",
  message: "Olá! Gostaria de um orçamento de DJ para o meu evento. Podemos conversar?",
};

// Estado do botão de liberar a partir dos campos do pedido e da cota do DJ.
// Vitalício (unlimited): libera na hora, sem cota, sem checkout e mesmo com o pedido lotado
// (a API já manda can_unlock: true nesse caso).
export const getUnlockState = (lead, unlocksRemaining, unlimited = false) => {
  if (lead.unlock_pending) return { disabled: true, label: "Pagamento em andamento", icon: "hourglass_top" };
  if (!lead.can_unlock && lead.slots_remaining === 0) return { disabled: true, label: "Pedido esgotado", icon: "block" };
  if (!lead.can_unlock) return { disabled: true, label: "Indisponível", icon: "block" };
  if (unlimited) return { disabled: false, label: "Liberar contato", icon: "lock_open" };
  if (unlocksRemaining > 0) {
    return { disabled: false, label: `Usar 1 das suas liberações (restam ${unlocksRemaining})`, icon: "lock_open", useQuota: true };
  }
  if (lead.unlock_price != null) {
    return { disabled: false, label: `Liberar por ${formatCents(lead.unlock_price)}`, icon: "lock_open" };
  }
  return { disabled: true, label: "Indisponível", icon: "block" };
};

// Pedido bloqueado (locked: true): só local, data e tipo são reais.
function LockedLeadCard({ lead, index, unlocksRemaining, unlimited, unlocking, onUnlock }) {
  const state = getUnlockState(lead, unlocksRemaining, unlimited);
  const soldOut = state.label === "Pedido esgotado";

  return (
    <Card id={index === 0 ? "lead-card-0" : undefined} sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDBox variant="gradient" bgColor="info" color="white" borderRadius="lg" coloredShadow="info" py={1} px={2}>
            <MDTypography variant="h6" color="white" fontWeight="medium">
              {lead.event_type || "Evento"}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" flexDirection="column" alignItems="flex-end" id={index === 0 ? "lead-status-0" : undefined}>
            <MDTypography variant="caption" color="text" fontWeight="bold">
              {formatLeadDate(lead.event_date)}
            </MDTypography>
            <MDBox mt={0.5}>
              {soldOut ? (
                <MDBadge badgeContent="Esgotado" color="secondary" variant="gradient" size="sm" />
              ) : (
                <MDBadge badgeContent="Bloqueado" color="dark" variant="gradient" size="sm" />
              )}
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="button" color="text" fontWeight="bold" display="block">
            Local: <span style={{ fontWeight: "normal" }}>{lead.location || "Não informado"}</span>
          </MDTypography>
          <MDBox aria-hidden="true" sx={{ filter: "blur(5px)", userSelect: "none" }}>
            <MDTypography variant="button" color="text" fontWeight="bold" display="block">
              Cliente: <span style={{ fontWeight: "normal" }}>{PLACEHOLDER.client}</span>
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="bold" display="block">
              Convidados: <span style={{ fontWeight: "normal" }}>{PLACEHOLDER.guests}</span>
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={2} p={2} bgColor="grey-100" borderRadius="lg" aria-hidden="true" sx={{ userSelect: "none" }}>
          <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
            Mensagem do Cliente:
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ filter: "blur(5px)" }}>
            &quot;{PLACEHOLDER.message}&quot;
          </MDTypography>
        </MDBox>

        {typeof lead.slots_remaining === "number" && lead.slots_remaining > 0 && (
          <MDTypography variant="caption" color="text" display="block" mb={1.5}>
            {lead.slots_remaining === 1 ? "Resta 1 vaga de DJ" : `Restam ${lead.slots_remaining} vagas de DJ`} neste pedido.
          </MDTypography>
        )}
        {unlimited && lead.slots_remaining === 0 && (
          <MDTypography variant="caption" color="text" display="block" mb={1.5}>
            Vagas de DJ esgotadas, mas seu plano vitalício libera mesmo assim.
          </MDTypography>
        )}

        <MDBox id={index === 0 ? "lead-actions-0" : undefined}>
          <MDButton
            variant="gradient"
            color={state.disabled ? "secondary" : "success"}
            fullWidth
            disabled={state.disabled || unlocking}
            onClick={() => onUnlock(lead, state)}
          >
            <Icon>{state.icon}</Icon>&nbsp;{unlocking ? "Liberando..." : state.label}
          </MDButton>
        </MDBox>
      </MDBox>
    </Card>
  );
}

LockedLeadCard.defaultProps = {
  unlimited: false,
  unlocking: false,
};

LockedLeadCard.propTypes = {
  lead: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    location: PropTypes.string,
    event_date: PropTypes.string,
    event_type: PropTypes.string,
    slots_remaining: PropTypes.number,
    can_unlock: PropTypes.bool,
    unlock_pending: PropTypes.bool,
    unlock_price: PropTypes.number,
  }).isRequired,
  index: PropTypes.number.isRequired,
  unlocksRemaining: PropTypes.number.isRequired,
  unlimited: PropTypes.bool,
  unlocking: PropTypes.bool,
  onUnlock: PropTypes.func.isRequired,
};

export default LockedLeadCard;
